import * as React from 'react';
import TimeEntry from 'api/immutables/ImmutableTimeEntry';
import { DateTime } from 'luxon';
import * as Styled from './styled';
import { Add } from '@material-ui/icons';
import { Tooltip } from '@material-ui/core';
import { ValidationState } from 'api/immutables/validators';
import TimeEntryDropTarget from '../../containers/Home/TimeEntryDropTarget';
import { getDateFormat } from '../../util/date';
import { AggregateTotals } from 'store/home.store';
import { TKConsumer } from 'common/TKProvider';
import { TimeKeeperAssignment } from 'api/types/types';
import { withTranslation } from 'react-i18next';
import { i18n } from 'i18next';
interface Props {
    entries: TimeEntry[];
    date: DateTime;
    onChange: (t: TimeEntry, newVState?: ValidationState, durVState?: boolean) => void;
    selected: number[];
    isMgmtDashboard?: boolean;
    onSelect: (ids: number[]) => void;
    expanded: number[];
    validationMap?: Map<number, ValidationState>;
    durValidationMap?: Map<number, boolean | undefined>;
    onExpand: (ids: number[]) => void;
    onCreateEntry?: (date: DateTime) => void;
    onPost: (t: TimeEntry) => void;
    onUnpost?: (t: TimeEntry) => void;
    onSave?: (t: TimeEntry) => void;
    onDelete?: (t: TimeEntry) => void;
    onCancel: (t: TimeEntry) => void;
    onCopy?: (t: TimeEntry) => void;
    onEdit: (t: TimeEntry) => void; 
    onSplit?: (t: TimeEntry) => void;
    onMerge?: (t: TimeEntry) => void;
    onTransfer?: (t: TimeEntry) => void;
    onApprove?: (t: TimeEntry) => void;
    onReject?: (t: TimeEntry) => void;
    aggregateTotals?: (date: DateTime[]) => AggregateTotals;
    onDrop?: () => void;
    buildCodeSets?: (id: number) => void;
    timeCastEnabledInDayView?: boolean;
    onSetFieldLoader?: (value: boolean) => void;
    // tslint:disable-next-line:no-any
    t: any;
    i18n: i18n;
}

class TimeEntryList extends React.Component<Props> {
    
    select = (id: number) => () => {
        let selected = this.props.selected.slice();
        if (selected.includes(id)) {
            selected = selected.filter((s) => s !== id);
        } else {
            selected.push(id);
        }
        this.props.onSelect(selected);
    }
    
    expand = (id: number) => () => {
        let expanded = this.props.expanded.slice();
        if (expanded.includes(id)) {
            expanded = expanded.filter((s) => s !== id);
        } else {
            expanded.push(id);
        }
        this.props.onExpand(expanded);
    }
    
    selectAll = () => {
        let entries = this.props.entries.filter((e) => !e.isPosted());
        let rest = this.props.selected.filter(s => entries.every((e) => e.id !== s));
        if (entries.every((e) => this.props.selected.includes(e.id as number))) {
            this.props.onSelect(rest);
        } else {
            this.props.onSelect(entries.map(e => e.id as number).concat(rest));
        }
    }
    
    onCreateEntry = () => {
        this.props.onCreateEntry!(this.props.date);
    }
    
    render() {
        const {
            date,
            entries,
            expanded, 
            selected,
            onChange,
            onPost,
            onUnpost,
            onSave,
            onDelete,
            onCancel,
            onCopy,
            onEdit,
            onSplit,
            onMerge,
            onTransfer,
            onApprove,
            onReject,
            durValidationMap,
            validationMap,
            onDrop,
            buildCodeSets,
            aggregateTotals,
            timeCastEnabledInDayView,
            onSetFieldLoader,
            isMgmtDashboard
        } = this.props;
        let allSelected = entries.every((e) => selected.includes(e.id as number));
        const totals = aggregateTotals! ? aggregateTotals!([date]) : undefined;
        let moreThanOneSelected = selected.length > 1;
        return (
            <>
                <TKConsumer>
                    { (tk: TimeKeeperAssignment) => 
                <Styled.TimeEntryListHeader>
                    {/* <Checkbox
                        checked={allSelected}
                        indeterminate={anySelected && !allSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={this.selectAll}
                        disableRipple={true}
                        style={{padding: 0}}
                    /> */}
                    <Styled.ListDate selected={allSelected} writable={!tk.writable} onClick={this.selectAll}>
                        {date.toFormat('EEE') + ', ' + date.toFormat(getDateFormat())}
                    </Styled.ListDate>
                    {totals &&
                        <>
                            {!isMgmtDashboard &&
                                <>
                                    <Styled.Posted justifyContent="flex-end">
                                        {this.props.t('list.row.info.posted') + ': ' + (totals.posted).toFixed(2)}
                                    </Styled.Posted>
                                    <Styled.Draft justifyContent="center">
                                        {this.props.t('list.row.info.draft') + ': ' + (totals.draft).toFixed(2)}
                                    </Styled.Draft>
                                </>
                            }
                            <Styled.TotalDuration justifyContent="left">
                                {this.props.t('list.row.info.total') + ': ' + (totals.posted + totals.draft).toFixed(2)}
                            </Styled.TotalDuration>
                        </>
                    }
                    {tk.writable && !isMgmtDashboard &&
                        <Styled.StyledIconButton
                            disableRipple={true}
                            onClick={this.onCreateEntry}
                        >
                            <Tooltip title={this.props.t('list.row.info.action.add.tooltip')}>
                                <Add />
                            </Tooltip>
                        </Styled.StyledIconButton>
                    }                    
                </Styled.TimeEntryListHeader>}
                </TKConsumer>
                {entries.map((t) =>
                    <Styled.TimeEntryItem key={t.id as number}>
                        <TimeEntryDropTarget
                            validationState={validationMap ? validationMap.get(t.id!) : undefined}
                            durValidationState={durValidationMap ? durValidationMap.get(t.id!) : undefined}
                            timeEntry={t}
                            onChange={onChange}
                            onExpand={this.expand(t.id as number)}
                            expanded={expanded.includes(t.id as number)}
                            onSelect={this.select(t.id as number)}
                            selected={selected.includes(t.id as number)}
                            onPost={onPost}
                            onUnpost={onUnpost}
                            onSave={onSave}
                            onDelete={onDelete}
                            onCancel={onCancel}
                            onCopy={onCopy}
                            onEdit={onEdit}
                            onSplit={onSplit}
                            onMerge={onMerge}
                            isMergeable={moreThanOneSelected}
                            onTransfer={onTransfer}
                            onApprove={onApprove}
                            onReject={onReject}
                            isTransferable={moreThanOneSelected}
                            onDrop={onDrop}
                            buildCodeSets={buildCodeSets}
                            timeCastEnabledInDayView={timeCastEnabledInDayView}
                            onSetFieldLoader={onSetFieldLoader}
                            isMgmtDashboard={isMgmtDashboard}
                        />
                    </Styled.TimeEntryItem>
                )}
            </>
        );
    }
}

export default withTranslation(['timeentries'])(TimeEntryList);